import React, { CSSProperties, FunctionComponent } from 'react';
import cn from '../cn';
import { cssVars as palette } from '../../theme/cssVars';

type SwitchColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

type SwitchUIProps = {
  checked?: boolean,
  onChange?: (e: any) => void,
  color?: SwitchColor,
  disabled?: boolean,
  name?: string,
  className?: string,
  style?: CSSProperties,
};

// Checked track colour per `color`. `default`/`primary` use the theme accent;
// the semantic colours keep their palette hue so the meaning survives a restyle.
const trackOn: Record<SwitchColor, string> = {
  default: 'var(--ds-accent)',
  primary: 'var(--ds-accent)',
  secondary: palette.secondary,
  error: palette.error,
  info: palette.info,
  success: palette.success,
  warning: palette.warning,
};
const TRACK_OFF = 'var(--ds-border-strong)';

// Design-system switch: 32x18 track + 14px thumb, thumb slides 14px and the
// track colours on. Controlled `checked`; toggles via onChange (synthetic with
// target.checked).
const SwitchUI: FunctionComponent<SwitchUIProps> = ({
  checked = false,
  onChange,
  color = 'primary',
  disabled = false,
  name,
  className = '',
  style,
}) => {
  const handleClick = () => {
    if (disabled) return;
    onChange?.({ target: { checked: !checked, name } });
  };

  return (
    <span
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn('relative inline-flex h-[18px] w-[32px] shrink-0 items-center', disabled ? 'cursor-default opacity-45' : 'cursor-pointer', className)}
      style={style}
    >
      {/* track */}
      <span
        className="absolute inset-0 rounded-full transition-colors duration-150"
        style={{ backgroundColor: checked ? trackOn[color] : TRACK_OFF }}
      />
      {/* thumb */}
      <span
        className="absolute left-[2px] h-[14px] w-[14px] rounded-full transition-transform duration-150"
        style={{
          transform: checked ? 'translateX(14px)' : 'translateX(0)',
          backgroundColor: '#fff',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        }}
      />
    </span>
  );
};

export default SwitchUI;
